/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'primereact/button';
import styled from 'styled-components';
import { useStoreContext } from '@/contexts/StoreContext';

interface FilterNavTopicsProps {
  selectedTopics: string[];
  onTopicChange: (topics: string[]) => void;
  onClearAll: () => void;
}

const FilterButton = styled(Button)`
  padding: 0.5rem 0.5rem;
  border-radius: 0.8rem;
  font-size: 0.8rem !important;
`;

export const FilterNavTopics: React.FC<FilterNavTopicsProps> = ({ selectedTopics, onTopicChange, onClearAll }) => {
  const { t } = useTranslation();
  const { antflowServerConfig } = useStoreContext();

  const toggleTopic = (topic: string) => {
    const newTopics = selectedTopics.includes(topic)
      ? selectedTopics.filter((t) => t !== topic)
      : [...selectedTopics, topic];
    onTopicChange(newTopics);
  };

  const favoriteTopics = antflowServerConfig?.filterTags?.toSorted() || [];

  if (favoriteTopics.length === 0) {
    return null;
  }

  return (
    <div className="w-full flex flex-column md:flex-row md:align-items-start lg:align-items-center gap-2 px-1 md:pr-2 py-2">
      <div className="w-full flex flex-column sm:flex-row align-items-start sm:align-items-center gap-2">
        <FilterButton
          icon="pi pi-filter text-xs"
          className="w-full sm:w-auto justify-content-center px-3 flex-shrink-0"
          iconPos="right"
          severity="secondary"
          data-pr-classname="text-sm"
          label={t(['misc.filters', 'Filters'])}
          badge={selectedTopics.length > 0 ? `${selectedTopics.length}` : undefined}
        />

        <div className="w-full flex flex-row flex-wrap align-items-center gap-1">
          {favoriteTopics.map((topic) => (
            <FilterButton
              key={topic}
              label={topic}
              onClick={() => toggleTopic(topic)}
              severity={selectedTopics.includes(topic) ? undefined : 'secondary'}
              outlined={!selectedTopics.includes(topic)}
            />
          ))}
        </div>
      </div>

      {selectedTopics.length > 0 && (
        <FilterButton
          text
          rounded
          size="small"
          className="align-self-start md:align-self-center flex-shrink-0"
          icon="pi pi-times-circle"
          iconPos="right"
          data-pr-classname="text-sm"
          tooltipOptions={{ position: 'top' }}
          tooltip={t(['actions.clear_all', 'Clear all'])}
          onClick={onClearAll}
        />
      )}
    </div>
  );
};
